import { PowerStationPerformanceData, ProcessedChartData, EChartsSeriesData, ChartColorConfig } from '../types/DailyReportPerformanceChart.types';
import { truncateNumber } from '../../../../Utils/NumberUtil';

const DEFAULT_CHART_COLORS: ChartColorConfig = {
  generatedEnergy: '#2D5DE7', 
  expectedEnergy: '#FC531F',    
  performanceRatio: '#FFC107',  
  operationAvailability: '#8CE49F',
};

const SPLITS = 5;

export const powerSortPowerStationByPerformanceRatio = (
  powerStation: PowerStationPerformanceData[]
): PowerStationPerformanceData[] => {
  return [...powerStation].sort((a, b) => {
    const prA = a.performanceRatio ?? 0;
    const prB = b.performanceRatio ?? 0;
    return prA - prB;
  });
};

export const calculateMaxLeftAxisValueWithMargin = (powerStation: PowerStationPerformanceData[]): number => {
  const values = powerStation
    .flatMap(station => [station.generatedEnergy, station.expectedEnergy])
    .filter((value): value is number => {
      return (value !== null) && (value !== undefined);
  });

  if (values.length === 0) return 100 * 1.15;

  const maxValue = Math.max(...values);
  return (Math.ceil(truncateNumber(maxValue*1.15) / SPLITS)) * SPLITS; // Round up to the next multiple of SPLITS for better visualization
};

export const calculateRightAxisValueWithMargin = (): number => {
  return Math.max(100, (Math.ceil(truncateNumber(100 * 1.15) / SPLITS)) * SPLITS); // Round up to the next multiple of SPLITS for better visualization
};

export const calculateAxisInterval = (maxValue: number): number => {
  return Math.ceil(maxValue / SPLITS);
};

export const formatChartValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return truncateNumber(value, 2).toString();
};


export const processChartData = (
  rawData: PowerStationPerformanceData[],
  colorConfig: ChartColorConfig = DEFAULT_CHART_COLORS
): ProcessedChartData => {
  if (!Array.isArray(rawData) || rawData.length === 0) {
    return {
      xAxisData: [],
      seriesData: [],
      maxLeftAxisValue: 100,
      maxRightAxisValue: 100,
    };
  }

  const powerSortedStation = powerSortPowerStationByPerformanceRatio(rawData);

  const xAxisData = powerSortedStation.map(station => station.powerStationName);

  const maxLeftAxisValue = calculateMaxLeftAxisValueWithMargin(powerSortedStation);
  const maxRightAxisValue = calculateRightAxisValueWithMargin();

  const seriesData: EChartsSeriesData[] = [
    {
      name: 'Energia Gerada (MWh)',
      type: 'bar',
      data: powerSortedStation.map(station => truncateNumber(station.generatedEnergy ?? 0, 2)),
      yAxisIndex: 0,
      itemStyle: { color: colorConfig.generatedEnergy },
      tooltip: {
        valueFormatter: (value: number | string) => `${value} MWh`,
      },
    },
    {
      name: 'Energia Esperada (MWh)',
      type: 'bar',
      data: powerSortedStation.map(station => truncateNumber(station.expectedEnergy ?? 0, 2)),
      yAxisIndex: 0,
      itemStyle: { color: colorConfig.expectedEnergy },
      tooltip: {
        valueFormatter: (value: number | string) => `${value} MWh`,
      },
    },
    {
      name: 'PR (%)',
      type: 'line',
      data: powerSortedStation.map(station => truncateNumber(station.performanceRatio ?? 0, 2)),
      yAxisIndex: 1,
      itemStyle: { color: colorConfig.performanceRatio },
      tooltip: {
        valueFormatter: (value: number | string) => `${value}%`,
      },
    },
    {
      name: 'Disponibilidade (%)',
      type: 'line',
      data: powerSortedStation.map(station => truncateNumber(station.operationAvailability ?? 0, 2)),
      yAxisIndex: 1,
      itemStyle: { color: colorConfig.operationAvailability },
      tooltip: {
        valueFormatter: (value: number | string) => `${value}%`,
      },
    },
  ];

  return {
    xAxisData,
    seriesData,
    maxLeftAxisValue,
    maxRightAxisValue,
  };
};

export const hasValidChartData = (data: PowerStationPerformanceData[]): boolean => {
  return Array.isArray(data) && data.length > 0;
};
